/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import { Trans, useTranslation } from "react-i18next";
import i18n from "../i18n/i18n.js";

import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import useMeetingNameTooltip from "../../hooks/useMeetingNameTooltip.js";

import { Images } from "../Common/Images.jsx";
import { CreateNewMeeting_api, EditMeeting_api, GetParticipantsForMeeting_api } from './CommonAPI'
import { useApiUiResponse } from "../../hooks/apiUiResponseHelper.js";
import useMeetingRecurrence from "../../hooks/useMeetingRecurrence.js";

//import "./css/Calendar.css";
//import "./css/style.css";
//import "./css/style-teams.css";
import CustomDatePicker from "../Common/CustomDatePicker";
import CustomSearchableDropdown from "../Common/CustomSearchableDropdown.jsx";
//import { BASE_URL, CreateNewMeeting, MeetingRoomListAndInfo_api, MapGrid_api, MapCanvas_api, GetParticipantsForMeeting } from "../../Config/apiConfig";
import { BASE_URL, CreateNewMeeting, MeetingRoomListAndInfo_api, MapGrid_api, MapCanvas_api, GetParticipantsForMeeting } from "../../config/ApiConfig";
import { CreateNewMeetingTimeGrid_api, FetchSpotsByZone_api } from '../Common/CommonAPI'
import { EMAIL_PATTERN } from "../../utils/CommonUtils";
import { set } from "date-fns";
import { getAccessToken, getFirstname, getLastname, getLoginId, getEmailID, getGuId } from "../../utils/SecurityService.js";
import {
  formatDate,
  formatDateToLong,
  formatReactString,
  getDisabledSlotsAndButtonState,
  getEndDateBy,
  isTimeBefore,
  MEETING_NAME_MAX_LENGTH,
  MONTHLY_REPEAT_TYPE,
  parseLocalDate,
  WEEKDAY_LONG_KEYS
} from "../../utils/CommonUtils.js";
import MeetingStepProgress from "./MeetingStepProgress.jsx";

// Recurrence state, labels and payload now live in useMeetingRecurrence, shared
// with BookMeeting so the two create-meeting flows cannot drift apart. The
// REPEAT_INTERVAL_UNIT_KEY / MONTHLY_* tables moved there with them.

// Legacy MonthlyRepeatOption (a weekday id) -> MonthlyRepeatType, for editing a
// meeting saved before v5.1. Only used as a fallback: if EditMeetingDetails
// carries MonthlyRepeatType we take it as-is. 0 meant "on day N" and anything
// else meant "on the <nth> <weekday>"; the old value could not express
// LAST_WEEK, so a pre-v5.1 "last Monday" meeting still opens as "fourth Monday"
// - the data to tell them apart was never stored. New saves are exact.
const monthlyRepeatTypeFromLegacyOption = (weekDayId) =>
  (Number(weekDayId) || 0) === 0
    ? MONTHLY_REPEAT_TYPE.NTH_DAY
    : MONTHLY_REPEAT_TYPE.NTH_WEEK;

// Edit mode Step 1 heading = the label of the post-event button that opened the
// edit, keyed by RescheduleType (same keys as EditMeetingButtons).
const EDIT_TITLE_KEY_BY_RESCHEDULE_TYPE = {
  1: "react_edit_all_events_in_series",
  2: "react_edit_this_event",
  3: "react_edit_this_and_following_events",
};

const NewMeetingModal = ({
  setIsLoading,
  isLoading,
  show,
  date,
  startTime,
  endTime,
  onClose,
  onConfirm,
  slotMinTime = "08:00:00",
  slotMaxTime = "23:00:00",
  onMeetingCreated,
  onMeetingCreatedWithDate,
  selectedZone = null,   // currently selected zone id from parent
  onZoneChange = null,
  zoneMap = {},
  mapType = undefined,
  setNotification,
  navSpotId,   // Navigated spot id arrives from other page to set as default in create meeting popup
  setShowScheduleMeetingModal,
  mode = "create",        // "create" (default) | "edit"
  editMeetingId,          // MeetingId of the meeting being edited (edit mode)
  rescheduleType,         // 2 = edit this event, 1 = edit all events in series, 3 = this and all following (edit mode)
  onOpenSeries,           // "Open series" (rescheduleType 3): parent reopens the modal as a series edit (1)
}) => {
  const { t } = useTranslation("common");
  const isEdit = mode === "edit";
  const isSingleEdit = isEdit && Number(rescheduleType) === 2; // hide repeat/end-date UI
  // "This and all following": attendees are read-only on Step 1 and the Step 2
  // attendee editor is skipped - they can only be changed by editing the series.
  const isFollowingEdit = isEdit && Number(rescheduleType) === 3;
  // "Edit all events in this series": the date is the series' start date, so the
  // Date label reads "Start date".
  const isSeriesEdit = isEdit && Number(rescheduleType) === 1;
  const { handleApiUiResponse, renderApiUi } = useApiUiResponse(t);
  const editPrefilledRef = useRef(false); // pre-fill editable fields only once per open
  const editMeetingDetailsRef = useRef(null); // raw EditMeetingDetails; source of defaults for non-selectable fields (edit mode)
  // RON-5980: { date, from, to, spotId } of the booking this edit opened on. The
  // meeting is not an obstacle to itself, so that one booking is drawn as the
  // green selection instead of a grey block and is left out of the availability
  // maths. Captured once per open, from the room the edit opened with.
  const originalEditSlotRef = useRef(null);
  const calendarRef = useRef(null);
  // The element the time-grid scrolls inside. Doubles as the hover root and as the
  // tooltip's upper bound: this calendar is height="auto", so FullCalendar builds
  // no internal scroller of its own (RON-5953).
  const [calendarScrollEl, setCalendarScrollEl] = useState(null);
  const getTooltipBoundsEl = useCallback(() => calendarScrollEl, [calendarScrollEl]);

  // Full meeting name on hover. Targets FullCalendar's own `.fc-event-title`
  // rather than the week grid's custom markup: this calendar's layout (clock icon,
  // time/title ordering) is CSS keyed to the default event DOM, so that DOM stays.
  useMeetingNameTooltip(calendarScrollEl, {
    getBoundsEl: getTooltipBoundsEl,
    titleSelector: ".fc-event-title",
    // This grid is inside a Bootstrap `.modal` (z-index 1055), and the tooltip is a
    // document.body child pitched below the modal layer on purpose. Without this the
    // tooltip was built and positioned correctly on hover, then painted behind the
    // popup that owns the grid - invisible.
    aboveModal: true,
  });
  const meetingNameRef = useRef(null);
  const timeSelectionRef = useRef(null);
  const endDateInputId = "meeting-end-date";
  const [isTimeSlotSelected, setIsTimeSlotSelected] = useState(false);
  const isProgrammaticSelection = useRef(false);
  const ignoreNextDateClick = useRef(false);
  const lastFetchKeyRef = useRef("");
  const activeFetchRequestIdRef = useRef(0);


  const guid = getGuId();
  const accessToken = getAccessToken();
  const PFirstName = getFirstname();
  const PLastName = getLastname();
  const PEmail = getEmailID();
  //const PId = localStorage.getItem("PId");
  const PId = getLoginId();
  const mapTypeForSelectedZone = typeof mapType !== "undefined" ? mapType : (selectedZone ? zoneMap[selectedZone] : undefined);

  // local spot list and selected spot (dropdown)
  const [selectedSpot, setSelectedSpot] = useState(null);
  const [spotsList, setSpotsList] = useState([]);
  const [spot, setSpot] = useState("");
  const [selectedDate, setSelectedDate] = useState(
    // parseLocalDate, not new Date(): callers (Map, My bookings) pass "YYYY-MM-DD", which
    // new Date() reads as UTC midnight = the previous day in UTC− timezones.
    date instanceof Date ? date : date ? parseLocalDate(date) : new Date()
  );
  const [holidaysZoneWise, setHolidaysZoneWise] = useState([]);
  const [capacity, setCapacity] = useState(0);
  const selectedSpotObj = useMemo(() => {
    return spotsList.find(s => String(s.SpotId) === String(spot));
  }, [spot, spotsList]);
  const holidaysSet = useMemo(
    () => new Set(holidaysZoneWise || []),
    [holidaysZoneWise]
  );

  // const spotType = selectedSpotObj?.SpotType ?? 0;
  const spotType = 3

  const [timeIntervals, setTimeIntervals] = useState({ start: "08:00:00", end: "19:00:00", RepeatOptionByLiftRestriction: 1, }); // Default time intervals

  const [maxDate, setMaxDate] = useState(null);
  // 🔁 Repeat Options State - shared with BookMeeting via useMeetingRecurrence.
  // End date and the enable-repeat switch stay local: each screen resets them on
  // its own terms, so the hook deliberately does not own them.
  const {
    repeatType,
    repeatInterval,
    weeklyRepeatOption,
    monthlyRepeatType,
    setRepeatType,
    setRepeatInterval,
    setWeeklyRepeatOption,
    setMonthlyRepeatType,
    setMonthlyChoiceId,
    repeatIntervalOptions,
    monthlyRecurrenceChoices,
    activeMonthlyChoiceId,
    showsWeekdayChips,
    changeRepeatType,
    toggleWeeklyDay,
    isWeeklyDaySelected,
    selectMonthlyChoice,
    resetRecurrence,
    getRecurrencePayload
  } = useMeetingRecurrence({ selectedDate });
  const [endDate, setEndDate] = useState(null);
  const [isRepeatEnabled, setIsRepeatEnabled] = useState(false);

  const [showGuestForm, setShowGuestForm] = useState(false);

  const buildApiUrl = (path) => `${BASE_URL.replace(/\/$/, "")}/${path.replace(/^\//, "")}`;

  const [repeatOptionsList, setRepeatOptionsList] = useState([]);
  const [weeklyOptionsList, setWeeklyOptionsList] = useState([]);
  const [monthlyOptionsList, setMonthlyOptionsList] = useState([]);


  /* const repeatTypeOptions = [
    { id: 0, name: t("MeetingRepeatOptionNoRepeat") },
    { id: 1, name: t("MeetingRepeatOptionDaily") },
    { id: 2, name: t("MeetingRepeatOptionWeekly") },
    { id: 4, name: t("MeetingRepeatOptionBiweekly") },
    { id: 3, name: t("MeetingRepeatOptionMonthly") },
  ];
  const monthlyRepeatOptions = [
    { id: "1", label: `${t("1st")} ${t("monday")}` },
    { id: "2", label: `${t("1st")} ${t("tuesday")}` },
    { id: "3", label: `${t("1st")} ${t("wednesday")}` },
    { id: "4", label: `${t("1st")} ${t("thursday")}` },
    { id: "5", label: `${t("1st")} ${t("friday")}` },
    { id: "6", label: `${t("1st")} ${t("saturday")}` },
    { id: "7", label: `${t("1st")} ${t("sunday")}` },

    { id: "8", label: `${t("2nd")} ${t("monday")}` },
    { id: "9", label: `${t("2nd")} ${t("tuesday")}` },
    { id: "10", label: `${t("2nd")} ${t("wednesday")}` },
    { id: "11", label: `${t("2nd")} ${t("thursday")}` },
    { id: "12", label: `${t("2nd")} ${t("friday")}` },
    { id: "13", label: `${t("2nd")} ${t("saturday")}` },
    { id: "14", label: `${t("2nd")} ${t("sunday")}` },

    { id: "15", label: `${t("3rd")} ${t("monday")}` },
    { id: "16", label: `${t("3rd")} ${t("tuesday")}` },
    { id: "17", label: `${t("3rd")} ${t("wednesday")}` },
    { id: "18", label: `${t("3rd")} ${t("thursday")}` },
    { id: "19", label: `${t("3rd")} ${t("friday")}` },
    { id: "20", label: `${t("3rd")} ${t("saturday")}` },
    { id: "21", label: `${t("3rd")} ${t("sunday")}` },

    { id: "22", label: `${t("4th")} ${t("monday")}` },
    { id: "23", label: `${t("4th")} ${t("tuesday")}` },
    { id: "24", label: `${t("4th")} ${t("wednesday")}` },
    { id: "25", label: `${t("4th")} ${t("thursday")}` },
    { id: "26", label: `${t("4th")} ${t("friday")}` },
    { id: "27", label: `${t("4th")} ${t("saturday")}` },
    { id: "28", label: `${t("4th")} ${t("sunday")}` },

    { id: "29", label: `${t("last")} ${t("monday")}` },
    { id: "30", label: `${t("last")} ${t("tuesday")}` },
    { id: "31", label: `${t("last")} ${t("wednesday")}` },
    { id: "32", label: `${t("last")} ${t("thursday")}` },
    { id: "33", label: `${t("last")} ${t("friday")}` },
    { id: "34", label: `${t("last")} ${t("saturday")}` },
    { id: "35", label: `${t("last")} ${t("sunday")}` },
  ]; */

  const [approvalInfo, setApprovalInfo] = useState("");
  const [approverList, setApproverList] = useState([]);
  const [needApproval, setNeedApproval] = useState(false);
  const [requestWindow, setRequestWindow] = useState({ hours: "", minutes: "" });
  const [releaseWindow, setReleaseWindow] = useState({ hours: "", minutes: "" });
  const parseApprovalInfo = (text = "") => {
    const numericParts = String(text).match(/\d+/g) || [];

    return {
      request: {
        hours: numericParts[0] || "00",
        minutes: numericParts[1] || "00"
      },
      release: {
        hours: numericParts[2] || "00",
        minutes: numericParts[3] || "00"
      }
    };
  };

  const [employee, setEmployee] = useState("");
  const [employeeList, setEmployeeList] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [externalGuests, setExternalGuests] = useState([]);
  // Edit mode: which attendee list to send when the list changed (Step 3 box).
  // "added_removed" (default) = send the edited/current list; "all" = send the
  // original pre-edit list. Only relevant when the box is shown.
  const [sendUpdateChoice, setSendUpdateChoice] = useState("added_removed");
  const [guestInput, setGuestInput] = useState({
    name: "",
    surname: "",
    email: "",
  });
  //const selectedEmployeeIds = PId + "," + selectedEmployees.map(e => e.EmployeeId).join(","); //  "Participants": for create new meeting API
  const selectedEmployeeIds =
    selectedEmployees && selectedEmployees.length > 0
      ? PId + "," + selectedEmployees.map(e => e.EmployeeId).join(",")
      : PId;
  const [checkedEmployees, setCheckedEmployees] = useState([]);

  // Edit mode: reconstruct the ORIGINAL attendee payload (as loaded when the edit
  // was opened) from EditMeetingDetails.Participants, in the same shape the API
  // expects. Used when the user picks "All attendees" on Step 3.
  const buildOriginalAttendees = () => {
    const src = editMeetingDetailsRef.current;
    const rawParticipants = Array.isArray(src?.Participants) ? src.Participants : [];
    const employeeIds = [];
    const guestParticipants = [];
    rawParticipants.forEach((p) => {
      const isGuest = String(p?.UserType || "").toLowerCase() === "guest";
      if (isGuest) {
        guestParticipants.push({
          FirstName: p.FirstName || "",
          LastName: p.LastName || "",
          PrimaryEmail: p.PrimaryEmail || "",
          Id: 0,
          NationalIdentificationId: p.NationalIdentificationId || "",
          UserType: "Guest",
        });
      } else if (p?.Id != null && String(p.Id) !== "") {
        employeeIds.push(String(p.Id));
      }
    });
    return { participants: employeeIds.join(","), guestParticipants };
  };

  // Edit mode: has the current attendee list (employees + guests) diverged from
  // the original meeting? Drives whether the Step 3 "who to update" box appears.
  const attendeesChanged = useMemo(() => {
    if (!isEdit || !editMeetingDetailsRef.current) return false;
    const eqSet = (a, b) => a.size === b.size && [...a].every((v) => b.has(v));

    const { participants: origParticipants, guestParticipants: origGuests } =
      buildOriginalAttendees();

    const origEmployeeSet = new Set(
      String(origParticipants || "").split(",").map((s) => s.trim()).filter(Boolean)
    );
    const currEmployeeSet = new Set(
      String(selectedEmployeeIds || "").split(",").map((s) => s.trim()).filter(Boolean)
    );

    const origGuestSet = new Set(
      origGuests.map((g) => (g.PrimaryEmail || "").trim().toLowerCase()).filter(Boolean)
    );
    const currGuestSet = new Set(
      externalGuests.map((g) => (g.PrimaryEmail || "").trim().toLowerCase()).filter(Boolean)
    );

    return !eqSet(origEmployeeSet, currEmployeeSet) || !eqSet(origGuestSet, currGuestSet);
    // editMeetingDetailsRef is a ref; recompute is driven by the current selection state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, selectedEmployeeIds, externalGuests]);

  const employeeOptions = employeeList
    ?.filter(
      (emp) =>
        !selectedEmployees.some(
          (sel) => String(sel.EmployeeId) === String(emp.EmployeeId)
        )
    )
    .map((emp) => ({
      value: String(emp.EmployeeId),
      label: emp.EmployeeEmail ? `${emp.EmployeeName} (${emp.EmployeeEmail})` : emp.EmployeeName,
    }));

  // add employee object to selectedEmployees (id from select)
  const addEmployeeById = (empId) => {
    if (!empId) return;

    const nextCount = totalParticipants + 1;
    if (capacity > 0 && nextCount > capacity) {
      setEmployee("");
      setErrors((prev) => ({
        ...prev,
        employeeCapacity: t("react_maximum_capacity_reached"),
        guestCapacity: ""
      }));
      return;
    }

    const empObj = employeeList.find(
      (x) => String(x.EmployeeId) === String(empId)
    );

    if (!empObj) return;

    setSelectedEmployees((prev) => {
      if (prev.some((s) => s.EmployeeId === empObj.EmployeeId)) return prev;
      return [...prev, empObj];
    });

    setEmployee("");
  };

  useEffect(() => {
    if (!isCapacityReached()) {
      setErrors((prev) => ({
        ...prev,
        employeeCapacity: "",
        guestCapacity: ""
      }));
    }
  }, [selectedEmployees, externalGuests, capacity]);

  const handleCheckboxChange = (empId) => {
    setCheckedEmployees((prev) =>
      prev.includes(empId)
        ? prev.filter((id) => id !== empId) // uncheck
        : [...prev, empId]                  // check
    );
  };
  const fetchEmployeesBySpotId = async (spotId) => {
    if (!spotId) return;
    try {
      //setIsLoading(true);
      const reqParams =
      {
        SpotId: spotId,
        Search: "",
        SelectedParticipant: "",
      };

      const response = await GetParticipantsForMeeting_api(reqParams);
      const participantList = response?.Data?.Participants || response?.Data?.Records?.Participants || [];

      const normalized = participantList.map((e) => ({
        EmployeeId: String(e.Id ?? e.EmployeeId ?? ""),
        EmployeeName: `${e.FirstName ?? ""} ${e.LastName ?? ""}`.trim(),
        EmployeeEmail: e.PrimaryEmail ?? e.Email ?? "",
      }));

      setEmployeeList(normalized);
    } catch (err) {
      console.error("Error fetching Employees:", err);
      setEmployeeList([]);
    }
    finally {
      //setIsLoading(false);
    }
  };

  const [meetingName, setMeetingName] = useState("");
  // The green success tick on Title only appears once the user has typed in it, not
  // for a title filled in on open (edit mode pre-fill).
  const [meetingNameTouched, setMeetingNameTouched] = useState(false);
  const [recurrence, setRecurrence] = useState("");
  const [notes, setNotes] = useState("");
  const [currentSection, setCurrentSection] = useState(0);

  // Participants: for create new meeting API (initial sample entries moved into state so we can append/remove)
  const [participants, setParticipants] = useState([]);
  const [checkedParticipants, setCheckedParticipants] = useState([]);

  const totalParticipants = 1 + selectedEmployees.length + externalGuests.length;

  const isCapacityReached = () => {
    return capacity > 0 && totalParticipants >= capacity;
  };

  const [selectedFrom, setSelectedFrom] = useState(startTime || "09:00");
  const [selectedTo, setSelectedTo] = useState(endTime || "09:30");

  const [showSlots, setShowSlots] = useState(false);
  // End-date hover tooltip, same pattern as BookMeeting.jsx
  const [showEndDateTooltip, setShowEndDateTooltip] = useState(false);
  const timePickerRef = useRef(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [blockedBookingTimes, setBlockedBookingTimes] = useState([]);
  const [availableRoomTimeIntervals, setAvailableRoomTimeIntervals] = useState([]);
  const [bookedRoomSlots, setBookedRoomSlots] = useState([]);
  const [zoneCurrentTime, setZoneCurrentTime] = useState("");
  const isSlotBooked = (from, to) => {
    return events.some(event => {
      const evStart = event.start.slice(11, 16);
      const evEnd = event.end.slice(11, 16);

      // check overlap
      return !(to <= evStart || from >= evEnd);
    });
  };
  const isBlockedBookingSlot = (from, to) => {
    return blockedBookingTimes.some((slot) => {
      const [blockedStartRaw, blockedEndRaw] = slot.split("-");

      if (!blockedStartRaw || !blockedEndRaw) return false;

      const blockedStart = blockedStartRaw.trim();
      const blockedEnd = blockedEndRaw.trim();

      return !(to <= blockedStart || from >= blockedEnd);
    });
  };

  const isManualSlotDisabled = (slot) => {
    const [from, to] = slot.split("-");

    if (!from || !to) return false;

    return (
      hasSlotEnded(to, selectedDate) ||
      isSlotBooked(from, to) ||
      isBlockedBookingSlot(from, to)
    );
  };

  const isManualSlotWithinSelectedRange = (slotStart, slotEnd) => {
    if (!selectedFrom || !selectedTo) return false;
    if (selectedFrom === "00:00" || selectedTo === "00:00") return false;

    return (
      toMinutes(slotStart) >= toMinutes(selectedFrom) &&
      toMinutes(slotEnd) <= toMinutes(selectedTo)
    );
  };

  const applyBlockedTimeSlotClasses = () => {
    setTimeout(() => {
      const calendarApi = calendarRef.current?.getApi();
      const calendarEl = calendarApi?.el;

      if (!calendarEl) return;

      const blockedStartTimes = new Set(
        blockedBookingTimes
          .map((slot) => slot.split("-")[0]?.trim())
          .filter(Boolean)
          .map((time) => (time.length === 5 ? `${time}:00` : time))
      );

      const slots = calendarEl.querySelectorAll(".fc-timegrid-slot-lane");

      slots.forEach((slot) => {
        const time = slot.getAttribute("data-time");

        slot.classList.remove("fc-past-timezone");

        if (time && blockedStartTimes.has(time)) {
          slot.classList.add("fc-past-timezone");
        }
      });
    }, 0);
  };

  // NEW: events state for FullCalendar and loading flag for bookings
  const [events, setEvents] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [isCalendarDataReady, setIsCalendarDataReady] = useState(false);

  const resetEndDate = () => {
    setIsRepeatEnabled(false);
    setEndDate(null);
  };

  // 🧠 Derived values
  const parsedDate = useMemo(() => {
    if (date instanceof Date) {
      return date;
    }

    if (!date) {
      return null;
    }

    const normalizedDate = parseLocalDate(date);
    return Number.isNaN(normalizedDate.getTime()) ? null : normalizedDate;
  }, [date]);
  const displaySelectedDate =
    selectedDate instanceof Date && !isNaN(selectedDate.getTime())
      ? `${String(selectedDate.getDate()).padStart(2, "0")} ${t(
        selectedDate.toLocaleString("en-GB", { month: "long" })
      )} ${selectedDate.getFullYear()}`
      : "";

  const [errors, setErrors] = useState({
    meetingName: "",
    timeInvalid: "",
    recurrenceDay: "",
    endDate: "",
    employeeCapacity: "",
    guestCapacity: "",
  });
  const meetingNamePattern = useMemo(() => {
    const raw = t("react_meeting_name_regex");
    // i18next returns the key itself when missing — guard against that.
    if (raw && raw !== "react_meeting_name_regex") {
      try {
        return new RegExp(raw);
      } catch {
        // malformed pattern in a locale file → fall through to default
      }
    }
    return /^(?!.*÷)[0-9a-zA-ZÀ-ÿĀ-ſ &,?\\'.()’-]+$/;
  }, [t, i18n.language]);

  const validateSectionOne = () => {
    let newErrors = {
      meetingName: "",
      timeInvalid: "",
      recurrenceDay: "",
      endDate: "",
      employeeCapacity: errors.employeeCapacity || "",
      guestCapacity: errors.guestCapacity || "",
    };
    let isValid = true;

    // 1️⃣ Meeting Name
    if (!meetingName || meetingName.trim() === "") {
      newErrors.meetingName = t("react_meeting_name_is_required");
      isValid = false;
    } else if (!meetingNamePattern.test(meetingName.trim())) {
      newErrors.meetingName = t("only_alphanumeric_characters_allowed");
      isValid = false;
    } else if (meetingName.length > MEETING_NAME_MAX_LENGTH) {
      newErrors.meetingName = t("meeting_name_maximum_length_is_s_characters")
        .replace("%s", MEETING_NAME_MAX_LENGTH);
      isValid = false;
    }

    // 2️⃣ From / To must exist
    if (!selectedFrom || !selectedTo) {
      newErrors.timeInvalid = t("please_select_time");
      isValid = false;
    }

    // 3️⃣ Cannot be 00:00
    if (
      !newErrors.timeInvalid &&
      (selectedFrom === "00:00" || selectedTo === "00:00")
    ) {
      newErrors.timeInvalid = t("please_select_time");
      isValid = false;
    }

    // 4️⃣ End > Start
    if (isRepeatEnabled && !endDate) {
      newErrors.endDate = t("please_select_end_date");
      isValid = false;
    }

    if (showsWeekdayChips && weeklyRepeatOption.length === 0) {
      newErrors.recurrenceDay = t("please_select_minimum_one_day");
      isValid = false;
    }

    /* if (selectedFrom >= selectedTo) {
      newErrors.timeInvalid = "End time must be greater than start time";
      isValid = false;
    } */

    setErrors(newErrors);
    return isValid;
  };

  const toMinutes = (time) => {
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const isSameDayAsToday = (value) => {
    if (!value) return false;

    const compareDate = parseLocalDate(value);
    const now = new Date();

    return compareDate.toDateString() === now.toDateString();
  };

  const hasSlotEnded = (slotEnd, slotDate = selectedDate) => {
    if (!slotDate || !slotEnd) return false;
    if (!isSameDayAsToday(slotDate)) return false;

    const now = new Date();
    const end = new Date(`${formatDate(slotDate)}T${slotEnd}:00`);

    return end <= now;
  };

  const { isNextButtonDisabled: isStep1NextDisabled } = useMemo(() => (
    getDisabledSlotsAndButtonState({
      timeIntervals: availableRoomTimeIntervals,
      blockedBookingTimeSlots: blockedBookingTimes,
      bookedSlots: bookedRoomSlots,
      startDate: formatDate(selectedDate),
      currentDate: formatDate(new Date()),
      currentTime: zoneCurrentTime,
      isTimeBefore,
    })
  ), [
    availableRoomTimeIntervals,
    blockedBookingTimes,
    bookedRoomSlots,
    selectedDate,
    zoneCurrentTime,
  ]);
  const shouldDisableStep1Details = currentSection === 0 && isStep1NextDisabled;

  // Retired with the monthly dropdown (RON-5827). It forced monthlyRepeatOption
  // away from 0 whenever Monthly was active, which the dropdown needed because 0
  // was not one of its items. With the radio group, 0 IS a first-class choice -
  // it is "On day N" - so this effect would immediately undo that selection, and
  // would also overwrite an edit-mode prefill of a day-of-month meeting.
  //
  // useEffect(() => {
  //   if (repeatType === 3 && monthlyRepeatOption === 0 && monthlyOptionsList.length > 0) {
  //     setMonthlyRepeatOption(Number(monthlyOptionsList[0].Value));
  //   }
  // }, [repeatType, monthlyRepeatOption, monthlyOptionsList]);

  // generate time slots when timeIntervals change
  useEffect(() => {
    if (!timeIntervals.start || !timeIntervals.end) return;

    const slots = [];
    let [startHour, startMin] = timeIntervals.start.split(":").map(Number);
    let [endHour, endMin] = timeIntervals.end.split(":").map(Number);

    const start = startHour + startMin / 60;
    const end = endHour + endMin / 60;

    for (let t = start; t < end; t += 0.5) {
      let h1 = Math.floor(t);
      let m1 = t % 1 === 0 ? "00" : "30";

      let h2 = Math.floor(t + 0.5);
      let m2 = (t + 0.5) % 1 === 0 ? "00" : "30";

      const s = `${String(h1).padStart(2, "0")}:${m1}`;
      const e = `${String(h2).padStart(2, "0")}:${m2}`;

      slots.push(`${s}-${e}`);
    }

    setTimeSlots(slots);
  }, [timeIntervals]);

  useEffect(() => {
    applyBlockedTimeSlotClasses();
  }, [blockedBookingTimes, selectedDate, spot, timeIntervals.start, timeIntervals.end]);

  // ✅ 1️⃣ Highlight selected time on calendar
  useEffect(() => {
    if (
      !calendarRef.current ||
      !selectedFrom ||
      !selectedTo ||
      selectedFrom === "00:00" ||
      selectedTo === "00:00"
    ) {
      isProgrammaticSelection.current = false;
      return;
    }

    if (hasSlotEnded(selectedTo, selectedDate)) {
      isProgrammaticSelection.current = false;
      return;
    }

    const calendarApi = calendarRef.current.getApi();
    calendarApi.unselect();

    const dateStr = formatDate(selectedDate); // yyyy-MM-dd

    const start = new Date(`${dateStr}T${selectedFrom}:00`);
    const end = new Date(`${dateStr}T${selectedTo}:00`);

    isProgrammaticSelection.current = true;
    calendarApi.select(start, end);

  }, [selectedFrom, selectedTo, selectedDate]);


  // ✅ 2️⃣ Reset all fields when modal opens
  useEffect(() => {
    if (show) {
      isProgrammaticSelection.current = false;
      lastFetchKeyRef.current = "";
      editPrefilledRef.current = false; // allow edit pre-fill to run once for this open
      originalEditSlotRef.current = null; // RON-5980: re-capture for this open
      setIsCalendarDataReady(false);

      const nextSelectedDate = parsedDate || (date ? parseLocalDate(date) : new Date());

      // Avoid re-setting the same date on open, which retriggers the initial room-info fetch.
      if (formatDate(selectedDate) !== formatDate(nextSelectedDate)) {
        setSelectedDate(nextSelectedDate);
      }
      setSpot("");
      setMeetingName("");
      setMeetingNameTouched(false);
      setSelectedFrom("00:00");
      setSelectedTo("00:00");
      setShowSlots(false);
      setNotes("");
      setEmployee("");
      setRecurrence("");
      setGuestInput({ name: "", surname: "", email: "" });
      setParticipants([]);
      setSelectedEmployees([]);

      setExternalGuests([]); // clear previous external guests
      setSendUpdateChoice("added_removed"); // reset Step 3 attendee-update choice
      editMeetingDetailsRef.current = null; // clear previous edit source
      setCurrentSection(0);

      resetRecurrence();
      setEndDate(null);

      // also clear calendar selection
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.unselect();
      }
    }
  }, [show, date, parsedDate]);

  useEffect(() => {
    if (spot) {
      fetchEmployeesBySpotId(spot);
    }
    else {
      setEmployeeList([]);
    }
  }, [spot]);
  useEffect(() => {
    if (!show) return;
    if (!selectedZone) {
      setSpotsList([]);
      setSpot("");
      setEvents([]);
      setBlockedBookingTimes([]);
      setHolidaysZoneWise([]);
      setIsCalendarDataReady(false);
      return;
    }
    if (!selectedDate || !spotType) return;

    const requestedSpotId = String(spot || navSpotId || "");
    const fetchKey = [
      selectedZone,
      formatDate(selectedDate),
      spotType,
      requestedSpotId,
    ].join("|");

    if (lastFetchKeyRef.current === fetchKey) return;

    lastFetchKeyRef.current = fetchKey;
    setIsCalendarDataReady(false);
    fetchBookingsForDate(selectedDate, requestedSpotId);
  }, [show, selectedDate, spotType, selectedZone, spot, navSpotId]);

  // Dead with the approval step (RON-5827): nothing sets currentSection to 3.
  // useEffect(() => {
  //   if (currentSection === 3 && !needApproval) {
  //     setCurrentSection(2); // fallback
  //   }
  // }, [currentSection, needApproval]);

  useEffect(() => {
    if (isTimeSlotSelected) {
      setTimeout(() => {
        meetingNameRef.current?.focus();
      }, 100);

      setIsTimeSlotSelected(false); // reset flag
    }
  }, [isTimeSlotSelected]);

  const clearMeetingNameSuccessState = () => {
    if (!meetingNameRef.current) return;

    meetingNameRef.current.classList.remove("ctm-success");
    meetingNameRef.current.closest(".ctm-form-control-textarea")?.classList.remove("ctm-success");
  };

  useEffect(() => {
    if (meetingName.length <= MEETING_NAME_MAX_LENGTH) return;

    clearMeetingNameSuccessState();
  }, [meetingName]);

  // Edit mode: map the API's EditMeetingDetails onto the create-meeting form state.
  // Participants are normalized the same way as the create flow (employees vs. guests).
  const applyEditMeetingDetails = (details = {}) => {
    editMeetingDetailsRef.current = details; // keep raw source for non-selectable payload defaults
    setMeetingName(details.Title || "");
    setNotes(details.Note || "");

    if (details.StartDate) {
      const d = parseLocalDate(details.StartDate);
      if (!Number.isNaN(d.getTime())) setSelectedDate(d);
    }
    setSelectedFrom(details.StartTime || "00:00");
    setSelectedTo(details.EndTime || "00:00");

    // Repeat options (kept in state even in single-event edit so the payload
    // carries the meeting's original recurrence values).
    setRepeatType(Number(details.RepeatOptionId) || 0);
    // Prefer the v5.1 MonthlyRepeatType if the response carries it; otherwise
    // derive it from the legacy weekday id so meetings saved before v5.1 still
    // open on a sensible row. See monthlyRepeatTypeFromLegacyOption.
    setMonthlyRepeatType(
      details.MonthlyRepeatType !== undefined && details.MonthlyRepeatType !== null
        ? Number(details.MonthlyRepeatType) || MONTHLY_REPEAT_TYPE.DEFAULT
        : monthlyRepeatTypeFromLegacyOption(details.MonthlyRepeatOptionWeekDayId)
    );
    // RepeatInterval is prefilled when present so "Repeat every N" reopens on
    // the saved count instead of silently resetting the series to every 1.
    setRepeatInterval(Number(details.RepeatInterval) || 1);

    const weekly = details.WeeklyRepeatOptionDayId;
    if (Array.isArray(weekly)) {
      setWeeklyRepeatOption(weekly.map(String));
    } else if (weekly !== undefined && weekly !== null && weekly !== "" && String(weekly) !== "0") {
      setWeeklyRepeatOption(String(weekly).split(",").map((v) => v.trim()).filter(Boolean));
    } else {
      setWeeklyRepeatOption([]);
    }

    if (details.EndDate) {
      const ed = parseLocalDate(details.EndDate);
      if (!Number.isNaN(ed.getTime())) {
        setEndDate(ed);
        setIsRepeatEnabled(true);
      }
    } else {
      setEndDate(null);
      setIsRepeatEnabled(false);
    }

    // Participants: split into employees and external guests, excluding the organiser
    // (the organiser id is always prepended on submit, like the create flow).
    const rawParticipants = Array.isArray(details.Participants) ? details.Participants : [];
    const employees = [];
    const guests = [];
    const uiParticipants = [];

    rawParticipants.forEach((p, idx) => {
      const id = String(p.Id ?? p.EmployeeId ?? "");
      const firstName = p.FirstName ?? "";
      const lastName = p.LastName ?? "";
      const fullName = `${firstName} ${lastName}`.trim() || p.EmployeeName || p.Name || "";
      const email = p.PrimaryEmail ?? p.Email ?? p.EmployeeEmail ?? "";
      const isGuest = String(p.UserType || "").toLowerCase() === "guest" || id === "0";

      if (String(id) === String(PId)) return; // organiser handled implicitly

      if (isGuest) {
        const guestKey = `guest-edit-${idx}-${id || email}`;
        guests.push({
          GuestKey: guestKey,
          FirstName: firstName,
          LastName: lastName,
          PrimaryEmail: email,
          Id: "0",
          NationalIdentificationId: p.NationalIdentificationId || "",
        });
        uiParticipants.push({ id: guestKey, name: fullName, email });
      } else {
        employees.push({
          EmployeeId: id,
          EmployeeName: fullName,
          EmployeeEmail: email,
        });
      }
    });

    setSelectedEmployees(employees);
    setExternalGuests(guests);
    setParticipants(uiParticipants);
  };

  // move fetchBookingsForDate out of useEffect so it can be reused
  const fetchBookingsForDate = async (d, selectedSpotId = spot) => {
    if (!d) return;
    const requestId = activeFetchRequestIdRef.current + 1;
    activeFetchRequestIdRef.current = requestId;
    setBookingsLoading(true);
    setIsCalendarDataReady(false);
    const dateStr = formatDate(d);
    try {
      console.log(spotType, " - spotType for CreateNewMeetingTimeGrid_api");
      const reqParams =
      {
        BookingDate: dateStr,
        SpotType: spotType,
        ZoneId: selectedZone,
      };
      // Edit mode: pull the meeting's editable details from the same endpoint.
      if (isEdit) {
        reqParams.RescheduleType = rescheduleType; // 2 = this event, 1 = whole series
        reqParams.MeetingId = editMeetingId;
      }
      console.log("CreateNewMeetingTimeGrid_api with params:", reqParams);
      const response = await CreateNewMeetingTimeGrid_api(reqParams);
      const records = response?.Data?.Records;
      console.log("Records at fetchBookingsForDate:", records);

      if (requestId !== activeFetchRequestIdRef.current) {
        return;
      }

      // Edit mode: a non-200 (e.g. after changing spot/space on step 1) must surface
      // the message in the orange alert popup and keep the user on step 1.
      if (isEdit && Number(response?.Data?.ResponseCode) !== 200) {
        handleApiUiResponse(response, {
          successToaster: 0,
          errorToaster: 1,
          alertPopup: 1,
        });
        setCurrentSection(0);
        return;
      }

      if (response?.Data?.ResponseCode === 200) {
        setRepeatOptionsList(response?.Data?.Info?.RepeatOption || []);
        setWeeklyOptionsList(response?.Data?.Info?.WeeklyRepeatOption || []);
        setMonthlyOptionsList(response?.Data?.Info?.MonthlyRepeatOption || []);
      }
      setHolidaysZoneWise(
        Array.isArray(records?.HolidaysZoneWise) ? records.HolidaysZoneWise : []
      );
      setZoneCurrentTime(records?.ZoneCurrentTime || "");
      const rooms = records?.AvailableRooms || [];
      const HourlyBookings = records?.AvailableHourlyBookings || [];
      const evts = [];

      // Map API data to FullCalendar events
      console.log("Processing bookings for date:", dateStr, "and rooms:", rooms);
      // ✅ bind spots dropdown
      /* setSpotsList(
        rooms.map(room => ({
          label: room.SpotName,
          value: String(room.SpotId)
        }))
      ); */
      const normalizedSpots = rooms.map(room => ({
        SpotId: String(room.SpotId),
        SpotName: room.SpotName,
        SpotType: Number(room.SpotType ?? 0),
      }));

      setSpotsList(normalizedSpots);
      const hasRequestedSpot = normalizedSpots.some(
        (room) => String(room.SpotId) === String(selectedSpotId)
      );
      const hasNavSpot = normalizedSpots.some(
        (room) => String(room.SpotId) === String(navSpotId)
      );
      const effectiveSpotId =
        (hasRequestedSpot ? String(selectedSpotId) : "") ||
        (hasNavSpot ? String(navSpotId) : "") ||
        normalizedSpots[0]?.SpotId ||
        "";

      console.log("navSpotId & normalizedSpots[0].SpotId:", navSpotId, normalizedSpots[0]?.SpotId);

      if (effectiveSpotId && String(spot) !== String(effectiveSpotId)) {
        setSpot(String(effectiveSpotId));
      }

      // RON-5980: remember the meeting's current booking the first time the edit
      // loads. EditMeetingDetails carries no SpotId, so the room is taken from the
      // fetch that opened the edit - which is the meeting's own room.
      if (isEdit && !originalEditSlotRef.current) {
        const editDetails = records?.EditMeetingDetails || response?.Data?.EditMeetingDetails;
        const editStart = editDetails?.StartDate ? parseLocalDate(editDetails.StartDate) : null;

        if (editDetails?.StartTime && editDetails?.EndTime && editStart && !Number.isNaN(editStart.getTime())) {
          originalEditSlotRef.current = {
            date: formatDate(editStart),
            from: String(editDetails.StartTime).slice(0, 5),
            to: String(editDetails.EndTime).slice(0, 5),
            spotId: String(effectiveSpotId || ""),
            // original title: the user may retype it, the bookings still carry this one
            title: String(editDetails.Title ?? "").trim(),
          };
        }
      }

      // The meeting being edited never blocks itself, on ANY date (Outlook-style):
      // a series returns its own occurrence on every day it runs. SelectedBookingTime
      // has no MeetingId, so an entry is "own" when it is in the meeting's room, at
      // EditMeetingDetails' StartTime/EndTime, under its original Title. The title
      // keeps another meeting at the same time in this room blocked.
      const ownEditSlot = originalEditSlotRef.current;
      const isOwnEditBooking = (start, end, meetingName) =>
        Boolean(ownEditSlot) &&
        String(ownEditSlot.spotId) === String(effectiveSpotId) &&
        ownEditSlot.from === start &&
        ownEditSlot.to === end &&
        ownEditSlot.title === String(meetingName ?? "").trim();

      const matchedRoom = rooms.filter(room => String(room.SpotId) === String(effectiveSpotId));
      const bookingsRoom = HourlyBookings.filter(room => String(room.SpotId) === String(effectiveSpotId));
      console.log("Matched Room:", matchedRoom);
      console.log("BookingsRoom Room:", bookingsRoom);
      if (matchedRoom) {
        matchedRoom.forEach(room => {
          console.log("Processing room:", room);
          room.SelectedBookingTime?.forEach((entry, idx) => {
            const slots = entry.BookedSlot;

            if (!Array.isArray(slots) || slots.length === 0) return;

            // ✅ First slot start
            const firstSlot = slots[0];
            const lastSlot = slots[slots.length - 1];

            const startTime = firstSlot.split("-")[0].trim();
            const endTime = lastSlot.split("-")[1].trim();

            // RON-5980: the meeting being edited shows as the green selection that
            // `calendarApi.select(...)` paints from selectedFrom/selectedTo. Drawing
            // it as an event would cover that highlight, and because isSlotBooked,
            // selectAllow and dateClick all test against `events`, it would also
            // refuse any new range that covers the meeting's current time.
            if (isOwnEditBooking(startTime, endTime, entry.MeetingName)) return;

            evts.push({
              id: `${room.SpotId}-${idx}`,
              title: `${entry.MeetingName} (${entry.Organizer})`,
              start: `${dateStr}T${startTime}:00`,
              end: `${dateStr}T${endTime}:00`,
              backgroundColor: "#D9D9D9",
              textColor: "#000",
              borderColor: "#fff",
              editable: false,
              overlap: false
            });
          });

        });

      }

      console.log("Events after processing entry:", evts);
      setEvents(evts);
      const selectedRoom = records?.AvailableRooms?.find(
        room => String(room.SpotId) === String(effectiveSpotId)
      );
      if (selectedRoom) {
        const flattenedBookedSlots = (selectedRoom.SelectedBookingTime || [])
          .filter((entry) => {
            const slots = entry?.BookedSlot;
            if (!Array.isArray(slots) || slots.length === 0) return true;

            // RON-5980: leaving the meeting's own slots in here lets the booking
            // being edited disable Step 1's Next button by making its own room
            // look fully booked.
            const entryStart = slots[0].split("-")[0].trim();
            const entryEnd = slots[slots.length - 1].split("-")[1].trim();

            return !isOwnEditBooking(entryStart, entryEnd, entry?.MeetingName);
          })
          .flatMap((entry) => entry?.BookedSlot || []);

        setBlockedBookingTimes(selectedRoom.BlockedBookingTime || []);
        setAvailableRoomTimeIntervals(selectedRoom.TimeIntervals || []);
        setBookedRoomSlots(flattenedBookedSlots);
        setCapacity(selectedRoom.Capacity || 0);
        console.log("Selected Room, selectedRoom.Capacity:", selectedRoom, selectedRoom.Capacity);
        console.log("selectedRoom.IsUserApprover,selectedRoom.IsApproversExist :", selectedRoom.IsUserApprover, selectedRoom.IsApproversExist);
        // Approval logic
        const approvalRequired =
          Number(selectedRoom?.IsUserApprover) === 0 &&
          Number(selectedRoom?.IsApproversExist) === 1;
        console.log("approvalRequired-", approvalRequired);
        setNeedApproval(approvalRequired);

        if (approvalRequired) {
          const approvalText = selectedRoom.ApprovalInfo || "";
          setApprovalInfo(approvalText);
          setApproverList(selectedRoom.ApproverList || []);

          // ApprovalInfo - PARSE HOURS & MINUTES HERE
          const parsed = parseApprovalInfo(approvalText);
          setRequestWindow(parsed.request);
          setReleaseWindow(parsed.release);
        } else {
          setApprovalInfo("");
          setApproverList([]);
          setRequestWindow({ hours: "", minutes: "" });
          setReleaseWindow({ hours: "", minutes: "" });
        }
        const start = selectedRoom.StartBookingTime;
        const end = selectedRoom.EndBookingTime;
        const RepeatOptionByLiftRestriction = selectedRoom.HideRepeatOptionByLiftRestriction

        setTimeIntervals({
          start,
          end,
          RepeatOptionByLiftRestriction:
            selectedRoom.HideRepeatOptionByLiftRestriction
        });

        if (
          Array.isArray(selectedRoom.RoomAvailableDays) &&
          selectedRoom.RoomAvailableDays.length > 0
        ) {
          const lastDate = selectedRoom.RoomAvailableDays[
            selectedRoom.RoomAvailableDays.length - 1
          ];

          console.log("Max available date for this room:", lastDate);

          setMaxDate(parseLocalDate(lastDate));
        }
      } else {
        setBlockedBookingTimes([]);
        setAvailableRoomTimeIntervals([]);
        setBookedRoomSlots([]);
      }

      // Edit mode: pre-fill editable fields ONCE from EditMeetingDetails so the
      // user's later room/date/time edits are not clobbered by refetches.
      if (isEdit && !editPrefilledRef.current) {
        const details =
          records?.EditMeetingDetails || response?.Data?.EditMeetingDetails;
        if (details) {
          applyEditMeetingDetails(details);
          editPrefilledRef.current = true;
        }
      }
    }
    catch (error) {
    }
    finally {
      if (requestId === activeFetchRequestIdRef.current) {
        setBookingsLoading(false);
        setIsCalendarDataReady(true);
      }
    }
  }

  // 🔹 Handlers
  // Close the slot panel on an outside click. It is absolutely positioned, so
  // without this it stays open over the rest of the form. Mirrors the mousedown
  // + contains pattern CustomSearchableDropdown already uses.
  useEffect(() => {
    if (!showSlots) return undefined;

    const handleOutsideClick = (event) => {
      if (timePickerRef.current && !timePickerRef.current.contains(event.target)) {
        setShowSlots(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showSlots]);

  const handleToggleSlots = () => setShowSlots(!showSlots);
  //const handleToggleSlots = () => setShowSlots((prev) => !prev);

  const handleSlotClick = (slot) => {
    if (bookingsLoading || !isCalendarDataReady) {
      return;
    }

    if (isManualSlotDisabled(slot)) {
      return;
    }

    setErrors((prev) => ({
      ...prev,
      timeInvalid: "",
    }));

    const clickedIndex = timeSlots.indexOf(slot);
    if (clickedIndex === -1) return;

    const selectedIndexes = timeSlots.reduce((indexes, currentSlot, index) => {
      const [slotStart, slotEnd] = currentSlot.split("-");

      if (isManualSlotWithinSelectedRange(slotStart, slotEnd)) {
        indexes.push(index);
      }

      return indexes;
    }, []);

    // Picking a slot resolves the panel. The one exception is clicking the only
    // selected slot, which clears the range - that is a deselect rather than a
    // choice, so the panel stays open for the user to pick a different slot.
    // (Extending a range therefore means reopening the panel: intended.)
    const isDeselect =
      selectedIndexes.length === 1 && selectedIndexes[0] === clickedIndex;

    if (!isDeselect) {
      setShowSlots(false);
    }

    if (selectedIndexes.length === 0) {
      const [start, end] = slot.split("-");
      setSelectedFrom(start);
      setSelectedTo(end);
      return;
    }

    const firstIndex = selectedIndexes[0];
    const lastIndex = selectedIndexes[selectedIndexes.length - 1];

    if (clickedIndex < firstIndex) {
      let nextStartIndex = firstIndex;

      for (let index = firstIndex - 1; index >= clickedIndex; index -= 1) {
        if (isManualSlotDisabled(timeSlots[index])) {
          break;
        }
        nextStartIndex = index;
      }

      const [start] = timeSlots[nextStartIndex].split("-");
      setSelectedFrom(start);
      return;
    }

    if (clickedIndex > lastIndex) {
      let nextEndIndex = lastIndex;

      for (let index = lastIndex + 1; index <= clickedIndex; index += 1) {
        if (isManualSlotDisabled(timeSlots[index])) {
          break;
        }
        nextEndIndex = index;
      }

      const [, end] = timeSlots[nextEndIndex].split("-");
      setSelectedTo(end);
      return;
    }

    if (clickedIndex === firstIndex && clickedIndex === lastIndex) {
      setSelectedFrom("00:00");
      setSelectedTo("00:00");
      return;
    }

    if (clickedIndex === firstIndex) {
      const [start, end] = timeSlots[firstIndex].split("-");
      setSelectedFrom(start);
      setSelectedTo(end);
      return;
    }

    if (clickedIndex === lastIndex) {
      const [, end] = timeSlots[lastIndex - 1].split("-");
      setSelectedTo(end);
      return;
    }

    // Keep the range from its start up to the clicked slot.
    const [, end] = timeSlots[clickedIndex].split("-");
    setSelectedTo(end);
  };

  const isCalendarInteractive =
    show &&
    !bookingsLoading &&
    isCalendarDataReady &&
    Boolean(selectedZone);

  const handleNext = () => {
    if (currentSection === 0 || currentSection < 2) {
      if (!validateSectionOne()) {
        const hasTimeError =
          !selectedFrom ||
          !selectedTo ||
          selectedFrom === "00:00" ||
          selectedTo === "00:00";
        const hasEndDateError = isRepeatEnabled && !endDate;

        setTimeout(() => {
          const modalBody = document.querySelector("#newmeetingsModal .modal-body");

          if (hasTimeError && timeSelectionRef.current) {
            timeSelectionRef.current.focus();

            if (modalBody) {
              modalBody.scrollTo({
                top: timeSelectionRef.current.offsetTop - 120,
                behavior: "smooth"
              });
            }

            return;
          }

          if (hasEndDateError) {
            const endDateInput = document.getElementById(endDateInputId);

            endDateInput?.focus();

            if (modalBody && endDateInput) {
              modalBody.scrollTo({
                top: endDateInput.offsetTop - 120,
                behavior: "smooth"
              });
            }

            return;
          }

          if ((!meetingName || meetingName.trim() === "") && meetingNameRef.current) {
            meetingNameRef.current.focus();

            if (modalBody) {
              modalBody.scrollTo({
                top: meetingNameRef.current.offsetTop - 120,
                behavior: "smooth"
              });
            }
          }
        }, 100);

        return;
      }
      // continue to next section...
      if (currentSection < 2) {
        // "This and all following" skips the Step 2 attendee editor.
        setCurrentSection((prev) => (isFollowingEdit ? 2 : prev + 1));
        setTimeout(() => {
          const modalBody = document.querySelector("#newmeetingsModal .modal-body");
          if (modalBody) modalBody.scrollTop = 0;
        }, 100);
      }
    }
  };

  const handleBack = () => {
    if (currentSection === 0) {
      setShowScheduleMeetingModal(false);
      return;
    }

    setCurrentSection((prev) => (isFollowingEdit && prev === 2 ? 0 : prev - 1));
  };

  const handleConfirm = async () => {
    try {
      console.log("needApproval from handleconfirm-", needApproval)
      setIsLoading(true);
      // Section 3 retired with the approval step (RON-5827) - kept for reference:
      // if (currentSection === 3) {
      //   setIsLoading(true);
      //   onClose();
      //   return;
      // }
      // onClose()
      const reqParams = {
        //BookingDate: parsedDate          ? formatDate(parsedDate)          : formatDate(new Date()),
        BookingDate: selectedDate ? formatDate(selectedDate) : formatDate(new Date()),
        EndDate: endDate ? formatDate(endDate) : "",
        MeetingName: meetingName,
        SpotId: Number(spot),
        StartTime: selectedFrom,
        EndTime: selectedTo,
        // v5.1 (RONLARAVEL-21): MonthlyRepeatOption is gone, replaced by
        // MonthlyRepeatType + RepeatInterval. All four recurrence parameters
        // come from useMeetingRecurrence, shared with BookMeeting, so create
        // and edit and both screens send the same shape.
        ...getRecurrencePayload(),
        Participants: selectedEmployeeIds,
        Note: notes,
        EndDateBy: getEndDateBy(endDate), // 2 when EndDate is sent, else 1 (create + edit)
        GuestParticipants: externalGuests,
      };

      // Edit mode: same payload shape as create + MeetingId, sent to EditMeeting_api.
      if (isEdit) {
        reqParams.MeetingId = editMeetingId;
        // RescheduleType tells EditMeeting_api which occurrences to update
        // (2 = this event, 1 = whole series). Same value sent to MeetingRoomListAndInfo.
        reqParams.RescheduleType = rescheduleType;
        // EditMeeting_api expects the API guest shape (numeric Id, UserType,
        // no UI-only GuestKey). Applied only here so the create payload is unchanged.
        reqParams.GuestParticipants = externalGuests.map((g) => ({
          FirstName: g.FirstName,
          LastName: g.LastName,
          PrimaryEmail: g.PrimaryEmail,
          Id: Number(g.Id) || 0,
          NationalIdentificationId: g.NationalIdentificationId || "",
          UserType: "Guest",
        }));

        // Recurrence isn't user-selectable in single-event edit, and the create
        // flow defaults an empty weekday list to "0" — an invalid WeeklyRepeatOption
        // (valid days are 1-7). For edit, default from the source meeting
        // (EditMeetingDetails) so non-weekly recurrence keeps "" instead of "0".
        const editSrc = editMeetingDetailsRef.current || {};
        const srcWeekly = editSrc.WeeklyRepeatOptionDayId;
        reqParams.WeeklyRepeatOption =
          Array.isArray(weeklyRepeatOption) && weeklyRepeatOption.length > 0
            ? weeklyRepeatOption.join(",")
            : Array.isArray(srcWeekly)
              ? srcWeekly.join(",")
              : (srcWeekly ?? "");

        // Step 3 attendee-update choice (only when the list actually changed):
        // "all" sends the ORIGINAL pre-edit attendees; "added_removed" (default)
        // keeps the edited/current list already built above.
        if (attendeesChanged && sendUpdateChoice === "all") {
          const original = buildOriginalAttendees();
          reqParams.Participants = original.participants;
          reqParams.GuestParticipants = original.guestParticipants;
        }

        console.log("Edit Meeting Parameters:", reqParams);
        const editData = await EditMeeting_api(reqParams);
        console.log("✅ Edit Meeting API Response:", editData);

        handleApiUiResponse(editData, {
          successToaster: 0, // success shown via parent setNotification (this modal unmounts on close)
          errorToaster: 1,
          alertPopup: 1,
        });

        if (editData?.Data?.ResponseCode === 200) {
          // Green success toaster in the still-mounted parent (modal closes below).
          setNotification?.({
            type: "success",
            title: t("success"),
            message: t("meeting_book_data_updated"),
          });
          if (typeof onMeetingCreated === "function") onMeetingCreated();
          if (typeof onMeetingCreatedWithDate === "function") {
            onMeetingCreatedWithDate(formatDate(selectedDate));
          }
          // Clear the shared parent loader. The parent's refetch shows its own
          // (isInitialLoading) spinner; leaving isLoading true here hangs the loader
          // forever because fetchData never resets isLoading.
          setIsLoading(false);
          setTimeout(() => onClose(), 200);
        } else {
          setIsLoading(false);
        }
        return;
      }

      console.log("Event Parameters:", reqParams);
      const data = await CreateNewMeeting_api(reqParams);

      console.log("✅ Create Meeting API Response:", data);

      if (data?.Data?.ResponseCode === 200) {

        //alert("✅ Meeting created successfully!");
        /* setNotification({
          type: "success",
          title: t("success"),
          message: t("react_meeting_created_successfully")
        }); */

        setNotification?.({
          type: "success",
          title: t("success"),
          // an approval booking is not confirmed yet, it is pending an approver
          message: t("react_meeting_created_successfully")
        });
        if (typeof onMeetingCreated === "function") {
          console.log("✅ onMeetingCreated go inside function check");
          onMeetingCreated();
          setEndDate(null);
        }

        if (typeof onMeetingCreatedWithDate === "function") {
          onMeetingCreatedWithDate(formatDate(selectedDate));
        }
        //setTimeout(() => onClose(), 600); // close modal

        // Approval no longer gets its own section - the notice is shown on the
        // confirmation step before Confirm - so every success just closes.
        setIsLoading(true);
        setTimeout(() => onClose(), 200);
      }
      else {
        setNotification?.({
          type: "error",
          title: data?.Data?.Message,
          message: data?.Data?.ErrorMessage || t("oops_something_went_wrong_try_again")
        });
        setIsLoading(false);

        console.log("Error occured!", data?.Data?.ErrorMessage);
      }
    } catch (error) {
      console.error("❌ Error creating meeting:", error);
      setNotification?.({
        type: "error",
        title: t("fail"),
        message: error?.message || t("oops_something_went_wrong_try_again")
      });
      setIsLoading(false);

    } finally {
      // setIsLoading(false);
      //onClose();
    }
  };

  // "GuestParticipants" handler for create new meeting API
  const handleConfirmExternalGuest = (e) => {
    e?.preventDefault();
    // #region Validation for Guest Input - start
    let newErrors = {
      firstname: "",
      lastname: "",
      email: ""
    };

    let isValid = true;

    if (!(guestInput.name || "").trim()) {
      newErrors.firstname = t("please_enter_name");
      isValid = false;
    }

    if (!(guestInput.surname || "").trim()) {
      newErrors.lastname = t("react_enter_valid_surname");
      isValid = false;
    }
    const emailPattern = EMAIL_PATTERN;
    const email = (guestInput.email || "").trim();

    if (!email || !emailPattern.test(email)) {
      newErrors.email = t("enter_your_valid_email_address");
      isValid = false;
    }

    setErrors(newErrors);
    //return isValid;

    // #endregion Validation for Guest Input - end
    if (isValid) {
      const nextCount = totalParticipants + 1;
      if (capacity > 0 && nextCount > capacity) {
        setErrors((prev) => ({
          ...prev,
          employeeCapacity: "",
          guestCapacity: t("react_maximum_capacity_reached")
        }));
        return;
      }

      const { name, surname, email, nationalIdentificationId } = guestInput;

      if (!(name || "").trim() || !(email || "").trim()) {
        return;
      }

      const guestKey = `guest-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
      const newGuest = {
        GuestKey: guestKey,
        FirstName: name.trim(),
        LastName: surname.trim(),
        PrimaryEmail: email.trim(),
        Id: "0",
        NationalIdentificationId: (nationalIdentificationId || "").trim()
      };

      // append guest to externalGuests array (allow multiple)
      setExternalGuests((prev) => [...(Array.isArray(prev) ? prev : []), newGuest]);

      // add a UI participant object with a unique id (avoid key collisions when Id === "0")
      const participantForUI = {
        id: guestKey,
        name: `${newGuest.FirstName} ${newGuest.LastName}`.trim(),
        email: newGuest.PrimaryEmail || "",
      };
      setParticipants((prev) => [...(Array.isArray(prev) ? prev : []), participantForUI]);

      // Clear inputs
      setGuestInput({ name: "", surname: "", email: "", nationalIdentificationId: "" });

      // Collapse the guest input section
      const collapseEl = document.getElementById("collapseExample");
      if (collapseEl) {
        collapseEl.classList.remove("show");
        collapseEl.setAttribute("aria-expanded", "false");
      }
      console.log("✅ External guest added:", newGuest);
      setShowGuestForm(false);
    }
  };

  const handleRemoveParticipant = (id) => {
    if (!checkedParticipants.includes(id)) return; // 🚫 not checked

    setParticipants((prev) => prev.filter((p) => p.id !== id));
    setExternalGuests((prev) =>
      prev.filter((guest) => guest.GuestKey !== id)
    );

    // cleanup checked state
    setCheckedParticipants((prev) =>
      prev.filter((pid) => pid !== id)
    );
    setErrors((prev) => ({
      ...prev,
      guestCapacity: "",
      employeeCapacity: ""
    }));
  };

  const handleParticipantCheckboxChange = (id) => {
    setCheckedParticipants((prev) =>
      prev.includes(id)
        ? prev.filter((pid) => pid !== id) // uncheck
        : [...prev, id]                    // check
    );
  };

  const handleRemoveEmployee = (empId) => {
    if (!checkedEmployees.includes(empId)) return; // 🚫 not checked

    setSelectedEmployees((prev) =>
      prev.filter((e) => e.EmployeeId !== empId)
    );

    // cleanup checked state
    setCheckedEmployees((prev) =>
      prev.filter((id) => id !== empId)
    );
    // ✅ clear capacity error immediately
    setErrors((prev) => ({
      ...prev,
      employeeCapacity: "",
      guestCapacity: ""
    }));
  };

  // 🔹 Safe early return AFTER hooks
  if (!show) return null;

  // RON-5827: offcanvas header title per step - same texts the per-step
  // popup-title markup used before this panel became an offcanvas. In edit mode
  // Step 1 shows the clicked edit button's text ("edit_meeting" if the type is unknown).
  const offcanvasTitle =
    currentSection === 0
      ? (isEdit
        ? t(EDIT_TITLE_KEY_BY_RESCHEDULE_TYPE[Number(rescheduleType)] ?? "edit_meeting")
        : t("react_new_meeting"))
      : currentSection === 1
        ? t("react_create_meeting_add_participants")
        : t("react_meeting_confirmation");


  // 🧩 JSX
  return (
    <>
      {/* Backdrop - replaces the inline rgba background the old modal wrapper had.
          Visibility is owned by React (this component only renders when `show`),
          so no Bootstrap Offcanvas instance and no data-bs-toggle is involved. */}
      <div className="offcanvas-backdrop fade show" />
      <div
        className={`offcanvas offcanvas-end show edit-meeting-offcanvas ${currentSection === 0 ? "edit-meeting-timegrid" : ""}`}
        id="editmeetingpopup" tabIndex="-1" aria-labelledby="editmeetingpopupLabel"
      >
        <div className="offcanvas-header">
          <div className="header-title">
            <h5>{offcanvasTitle}</h5>
            <p>{selectedSpotObj?.SpotName || ""}</p>
          </div>
          {/* Step indicator - currentSection is passed raw; MeetingStepProgress
              clamps it onto the last circle for the confirmation/approval screens. */}
          <MeetingStepProgress step={currentSection} activeTab="meeting" />
          <div className="close-modal">
            <img
              src={Images.Close}
              alt="close"
              onClick={onClose}
              style={{ cursor: "pointer" }}
            />
          </div>
        </div>


        <div className="offcanvas-body">
          <div className="offcanvas-row d-flex">
            <div className="offcanvas-col-form">
              <form>
                {/* STEP 1 */}
                {currentSection === 0 && (
                  <div className="section active">

                    <div className="form-row">
                      <div className="form-col-1">
                        <label className="popup-inner-title m-0">{t("title")}*</label>
                      </div>
                      <div className="form-col-2">
                        <input
                          type="text"
                          ref={meetingNameRef}
                          disabled={shouldDisableStep1Details}
                          className={`form-control 
                        ${errors.meetingName ? "error-form" : ""}
                        ${!errors.meetingName && meetingName && meetingNameTouched ? "success-validation" : ""}
                        `}
                          value={meetingName}
                          onChange={(e) => {
                            const value = e.target.value;
                            /* if (value && !meetingNamePattern.test(value)) {
                              setErrors((prev) => ({
                                ...prev,
                                meetingName: t("only_alphanumeric_characters_allowed")
                              }));
                              setTimeout(() => {
                                clearMeetingNameSuccessState();
                              }, 0);
                            } else */
                            if (value.length > MEETING_NAME_MAX_LENGTH) {
                              setErrors((prev) => ({
                                ...prev,
                                meetingName: t("meeting_name_maximum_length_is_s_characters")
                                  .replace("%s", MEETING_NAME_MAX_LENGTH)
                              }));
                              setTimeout(() => {
                                clearMeetingNameSuccessState();
                              }, 0);
                            } else {
                              setErrors((prev) => ({ ...prev, meetingName: "" }));
                            }

                            setMeetingName(value);
                            setMeetingNameTouched(true);
                          }}

                        />

                        {errors.meetingName && (

                          <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.meetingName}</p></div>
                        )}
                      </div>
                    </div>

                    {/* "This and all following": read-only attendee names (organiser,
                        employees, guests) + "Open series", which reopens the modal as
                        an "Edit all events in this series" edit. */}
                    {isFollowingEdit && (
                      <div className="form-row align-items-start">
                        <div className="form-col-1 pt-3">
                          <label className="popup-inner-title m-0">{t("react_participants")}</label>
                        </div>
                        <div className="form-col-2">
                          <ul className="readonly-attendees-list">
                            <li>{`${PFirstName} ${PLastName}`.trim()}</li>
                            {selectedEmployees.map((emp) => (
                              <li key={emp.EmployeeId}>{emp.EmployeeName}</li>
                            ))}
                            {participants.map((p) => (
                              <li key={p.id}>{p.name}</li>
                            ))}
                          </ul>

                          <div className="open-series-wrap">
                            <a
                              href="#"
                              className="green-filled-with-icon icon-right"
                              onClick={(e) => {
                                e.preventDefault();
                                if (!isLoading) onOpenSeries?.();
                              }}
                            >
                              {t("react_open_series")}
                              <img src={Images.SeriesArrowIcon} alt="" />
                            </a>
                            <div className="open-series-info">
                              <img src={Images.InformationBlackIcon} alt="" />
                              <p>{t("react_only_change_attendees_by_editing_series")}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="form-row">
                      <div className="form-col-1">
                        <label className="popup-inner-title m-0">{t("Room")}</label>
                      </div>
                      <div className="form-col-2">
                        <CustomSearchableDropdown
                          items={spotsList || []}
                          value={spot || ""}     // keep string
                          labelKey="SpotName"
                          valueKey="SpotId"
                          searchable={false}
                          placeholder={
                            selectedZone
                              ? t("react_open_this_select_menu")
                              : t("please_select_zone_first")
                          }
                          disabled={!selectedZone}
                          onChange={(spotId) => {
                            setEvents([]);
                            setIsCalendarDataReady(false);
                            setSpot(spotId);    // keep string
                          }}
                        />
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-col-1"> <label className="popup-inner-title m-0" htmlFor="CustomDatePicker">
                        {isSeriesEdit ? t("StartDate") : t("date")}
                      </label>
                      </div>
                      <div className="form-col-2">
                        {/* PASS selected and onChange so we fetch when user picks a date */}
                        <CustomDatePicker
                          // id="datepicker"
                          selected={selectedDate}
                          preventMonthAutoSelect={true}
                          onChange={(d) => {
                            const newDate = d ? d : new Date();
                            // Create resets the time. Edit keeps the meeting's filled
                            // time (pre-filled from EditMeetingDetails, or as changed by
                            // the user) across date changes, like every other edit field.
                            if (!isEdit) {
                              setSelectedFrom("00:00");
                              setSelectedTo("00:00");
                            }
                            setSelectedDate(newDate);
                          }}
                          minDate={new Date()} // optional: disallow past dates
                          maxDate={maxDate}
                          filterDate={(dateValue) => !holidaysSet.has(formatDate(dateValue))}
                          onKeyDown={(e) => e.preventDefault()}
                          onChangeRaw={(e) => e.preventDefault()}
                        />
                      </div>
                    </div>

                    {/* Select Time Slots - the "select timeslot" label is now this row's
                    left column instead of a separate label-only row. */}
                    <div className="form-row">
                      <div className="form-col-1">
                        <label className="popup-inner-title m-0">{t("time")}</label>
                      </div>
                      <div className="form-col-2">
                        <div className="custom-timepicker" ref={timePickerRef}>
                          <div
                            className={`time-selection ${selectedFrom && selectedTo && !(selectedFrom === "00:00" && selectedTo === "00:00")
                              ? "ctm-success"
                              : ""
                              }`}
                            ref={timeSelectionRef}
                            tabIndex={-1}
                            onClick={handleToggleSlots}
                            id="selectedBox"
                          >
                            <div className="d-flex">
                              <div className="position-relative rightArrow">
                                <div className="time-alignment">
                                  <label>{t("from")}</label>
                                  <input
                                    type="text"
                                    readOnly
                                    value={selectedFrom === "00:00" ? "" : selectedFrom}
                                    placeholder="00:00"
                                  />
                                </div>
                              </div>

                              <div className="position-relative">
                                <div className="time-alignment">
                                  <label>{t("react_to")}</label>
                                  <input
                                    type="text"
                                    readOnly
                                    value={selectedTo === "00:00" ? "" : selectedTo}
                                    placeholder="00:00"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          {errors.timeInvalid && (
                            <div className="error-text">
                              <img src={Images.ErrorMsg} alt="" />
                              <p>{errors.timeInvalid}</p>
                            </div>
                          )}

                          {showSlots && (
                            <div className="slot-container d-grid mb-3">
                              {timeSlots.map((slot, idx) => {
                                const [start, end] = slot.split("-");
                                const isDisabled = isManualSlotDisabled(slot);
                                const isSelected = !isDisabled && isManualSlotWithinSelectedRange(start, end);

                                return (
                                  <div
                                    key={idx}
                                    className={`slot ${isDisabled ? "disabled" : ""} ${isSelected ? "active" : ""
                                      }`.trim()}
                                    onClick={() => !isDisabled && handleSlotClick(slot)}
                                    style={
                                      isSelected
                                        ? {
                                          background: "var(--color-theme-green)",
                                          color: "#fff",
                                          borderColor: "var(--color-theme-green)",
                                        }
                                        : undefined
                                    }
                                  >
                                    {slot}
                                  </div>
                                );
                              })}

                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/*Repeat Options Section */}
                    {!isSingleEdit && timeIntervals.RepeatOptionByLiftRestriction === 0 && (
                      <div className="form-row">
                        <div className="form-col-1">
                          <label className="popup-inner-title m-0">{t("recurrence")}</label>
                        </div>
                        <div className="form-col-2">
                          <CustomSearchableDropdown
                            items={repeatOptionsList}
                            labelKey="Label"
                            valueKey="Value"
                            value={repeatType}
                            disabled={shouldDisableStep1Details}
                            //placeholder="Select Repeat Type"
                            searchable={false}   // 🔥 THIS disables search box
                            onChange={(value) => {
                              // Recurrence values and their per-type resets live
                              // in the shared hook; the end date is this screen's
                              // own state, so it is reset here.
                              changeRepeatType(value);
                              resetEndDate();
                              setErrors((prev) => ({
                                ...prev,
                                recurrenceDay: ""
                              }));
                            }}
                          />

                        </div>
                      </div>
                    )}

                    {!isSingleEdit && (
                      <>
                        {/* Repeat every N Week(s)/Month(s). Sits directly under
                            the recurrence dropdown, so it precedes both the
                            weekday chips (Weekly) and the monthly pattern
                            dropdown (Monthly). See REPEAT_INTERVAL_UNIT_KEY for
                            why Daily and Biweekly are excluded. */}
                        {repeatIntervalOptions.length > 0 && (
                          <div className="form-row  border-0">
                            <div className="form-col-1">
                              <label className="popup-inner-title m-0">{t("edit_meeting_repeat_every")}</label>
                            </div>
                            <div className="form-col-2">
                              <CustomSearchableDropdown
                                items={repeatIntervalOptions}
                                labelKey="Label"
                                valueKey="Value"
                                value={repeatInterval}
                                disabled={shouldDisableStep1Details}
                                searchable={false}
                                onChange={(value) => setRepeatInterval(Number(value))}
                              />
                            </div>
                          </div>
                        )}

                        {showsWeekdayChips && (
                          <div className="form-row">
                            <div className="form-col-1">
                              {/* <label className="popup-inner-title m-0">{t("react_select_days")}</label> */}
                            </div>
                            <div className="form-col-2">
                              <div className="inline-btn-tags tags-btn d-flex flex-wrap gap-2">
                                {weeklyOptionsList.map((day) => (
                                  <button
                                    key={day.Value}
                                    type="button"
                                    disabled={shouldDisableStep1Details}
                                    onClick={() => {
                                      toggleWeeklyDay(day.Value);
                                      setErrors((prev) => ({
                                        ...prev,
                                        recurrenceDay: ""
                                      }));
                                    }}
                                    className={`${isWeeklyDaySelected(day.Value) ? "selected" : ""
                                      }`}
                                  >
                                    {day.Label}
                                  </button>
                                ))}

                              </div>
                              {errors.recurrenceDay && (
                                <div className="error-text">
                                  <img src={Images.ErrorMsg} alt="" />
                                  <p>{errors.recurrenceDay}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Superseded by the radio group below (RON-5827).
                            Kept, commented, until the new control is signed off.

                        {repeatType === 3 && (
                          <div className="form-row">
                            <div className="form-col-1"></div>
                            <div className="form-col-2">
                              <CustomSearchableDropdown
                                items={monthlyOptionsList}
                                value={monthlyRepeatOption}
                                labelKey="Label"
                                valueKey="Value"
                                disabled={shouldDisableStep1Details}
                                searchable={false}      // Search disabled
                                onChange={(value) => {
                                  setMonthlyRepeatOption(Number(value));   // same behavior
                                  setEndDate(null);
                                  setWeeklyRepeatOption([]);
                                }}
                              />

                            </div>
                          </div>
                        )}
                        */}

                        {/* Monthly "repeat on" choices, Outlook-style. The set is
                            derived from the selected date, so it follows the date
                            picker without any extra wiring. */}
                        {monthlyRecurrenceChoices.length > 0 && (
                          <div className="form-row">
                            <div className="form-col-1"></div>
                            <div className="form-col-2 pt-0">
                              <div className="participants-list mt-0 border-top-0 py-0 new-meeting-checkbox">
                                {monthlyRecurrenceChoices.map((choice) => (
                                  <div
                                    key={choice.id}
                                    className={`form-check d-flex align-items-center border-0 p-0 mb-3 ${activeMonthlyChoiceId === choice.id ? "check-active" : ""}`}
                                  >
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      name="monthlyRecurrenceChoice"
                                      id={`monthly-choice-${choice.id}`}
                                      checked={activeMonthlyChoiceId === choice.id}
                                      disabled={shouldDisableStep1Details}
                                      onChange={() => {
                                        selectMonthlyChoice(choice);
                                        setEndDate(null);
                                      }}
                                    />

                                    <label
                                      className="form-check-label ms-2"
                                      htmlFor={`monthly-choice-${choice.id}`}
                                    >
                                      {choice.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {repeatType !== 0 && (
                          <div className="form-row">
                            {/* Enable Repeat Switch - label and toggle share the left column */}
                            <div className="form-col-1">
                              <div className="form-date-switch d-flex flex-column align-items-start gap-2">
                                <div className="d-flex align-items-center gap-2">
                                  <label className="popup-inner-title m-0">{t("EndDate")}</label>

                                  {/* Info tooltip - mirrors the End date tooltip in
                                      BookMeeting.jsx, reusing .custom-tooltip-meeting.
                                      Focus handlers as well as hover, so it is not
                                      mouse-only. */}
                                  <span
                                    className="position-relative d-inline-flex"
                                    style={{ cursor: "pointer" }}
                                    tabIndex={0}
                                    aria-label={t("end_date")}
                                    onMouseEnter={() => setShowEndDateTooltip(true)}
                                    onMouseLeave={() => setShowEndDateTooltip(false)}
                                    onFocus={() => setShowEndDateTooltip(true)}
                                    onBlur={() => setShowEndDateTooltip(false)}
                                  >
                                    <img src={Images.TooltipIcon} alt="" />

                                    {showEndDateTooltip && (
                                      <div className="custom-tooltip-meeting position-absolute">
                                        <div className="tooltip-title d-flex align-items-center gap-2">
                                          <img src={Images.InfoIcon} className="img-fluid" alt="" />
                                          <h6>{t("end_date")}</h6>
                                        </div>
                                        <div className="tooltip-desc">
                                          <div className="d-flex align-items-center gap-2 mb-2">
                                            <img src={Images.GreenToggle} className="img-fluid" alt="" />
                                            <p>
                                              <Trans
                                                i18nKey="react_to_set_end_date_in_meeting"
                                                components={{ 1: <span className="text-green" /> }}
                                              />
                                            </p>
                                          </div>

                                          <div className="d-flex align-items-center gap-2">
                                            <img src={Images.GrayToggle} className="img-fluid" alt="" />
                                            <p>
                                              <Trans
                                                i18nKey="react_to_set_recurring_meeting"
                                                components={{ 1: <span className="text-green" /> }}
                                              />
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </span>
                                </div>

                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="remind-email"
                                    disabled={shouldDisableStep1Details}
                                    checked={isRepeatEnabled}
                                    onChange={(e) => {
                                      const checked = e.target.checked;
                                      setIsRepeatEnabled(checked);
                                      setErrors((prev) => ({
                                        ...prev,
                                        endDate: "",
                                      }));

                                      if (!checked) {
                                        setEndDate(null);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                            </div>

                            {/* End Date */}
                            <div className="form-col-2">
                              <CustomDatePicker
                                id={endDateInputId}
                                selected={endDate}
                                onChange={(date) => {
                                  setEndDate(date);
                                  setErrors((prev) => ({
                                    ...prev,
                                    endDate: "",
                                  }));
                                }}
                                minDate={new Date()}
                                filterDate={(dateValue) => !holidaysSet.has(formatDate(dateValue))}
                                disabled={shouldDisableStep1Details || !isRepeatEnabled}
                                placeholderText={isRepeatEnabled ? "--" : t("indefinitely")}
                                onKeyDown={(e) => e.preventDefault()}
                                onChangeRaw={(e) => e.preventDefault()}

                              />
                              {errors.endDate && (
                                <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.endDate}</p></div>
                              )}
                            </div>
                          </div>
                        )}

                      </>
                    )}

                    <div className="form-row border-0">
                      <div className="form-col-1">
                        <label className="popup-inner-title m-0">{t("description")}</label>
                      </div>
                      <div className="form-col-2">
                        <textarea
                          className="form-control"
                          placeholder={t("meeting_note")}
                          disabled={shouldDisableStep1Details}
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 2 */}
                {currentSection === 1 && (
                  <div className="section active">
                    <div className="form-row border-0">
                      <div className="form-col-1">
                        <label className="popup-inner-title m-0">{t("add_employee")}</label>
                      </div>
                      <div className="form-col-2">
                        <div className="custom-dropdown green-icon-drop-down mb-3">
                          <CustomSearchableDropdown
                            items={employeeOptions || []}
                            value={employee}  // string
                            labelKey="label"
                            valueKey="value"
                            searchable={true}
                            placeholder={t("name")}
                            searchPlaceholder={t("react_participant_search")}
                            onChange={(empId) => {  // empId is string
                              //console.log("Selected employee ID:", empId);
                              setEmployee(empId);    // store string ID
                              addEmployeeById(empId);
                            }}
                          />
                        </div>
                        {errors.employeeCapacity && (
                          <div className="error-text">
                            <img src={Images.ErrorMsg} alt="" />
                            <p>
                              {errors.employeeCapacity}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="form-row border-0">
                      <div className="form-col-1">
                        <div className="modal-btn">
                          <a
                            className="green-filled-with-icon"
                            role="button"
                            onClick={() => setShowGuestForm(prev => !prev)}
                          >
                            <span><img src={Images.GreenPlus} /></span>
                            {t("react_add_external_guest")}
                          </a>



                        </div>
                      </div>
                      <div className="form-col-2 pt-4 pb-5"></div>

                    </div>
                    {showGuestForm && (
                      <div className="card border-0">
                        <div className="form-row border-0">
                          <div className="form-col-1">
                            <label className="popup-inner-title m-0" htmlFor="name">{t("name") + "*"}</label>
                          </div>
                          <div className="form-col-2">
                            <input
                              type="text"
                              className={`form-control 
                              ${errors.firstname ? "error-form" : ""}
                              ${!errors.firstname && guestInput.name ? "success-validation" : ""}
                              `}
                              autoComplete="off"
                              placeholder={t("firstName")}
                              value={guestInput.name}
                              onChange={(e) => {
                                setGuestInput({ ...guestInput, name: e.target.value });
                                setErrors((prev) => ({ ...prev, firstname: "" }));
                              }}
                            />

                            {errors.firstname && (

                              <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.firstname}</p></div>
                            )}
                          </div>
                        </div>
                        <div className="form-row border-0">
                          <div className="form-col-1">
                            <label className="popup-inner-title m-0">{t("react_surname") + "*"}</label>
                          </div>
                          <div className="form-col-2 pt-0">
                            <input
                              type="text"
                              className={`form-control 
                              ${errors.lastname ? "error-form" : ""}
                              ${!errors.lastname && guestInput.surname ? "success-validation" : ""}
                              `}
                              autoComplete="off"
                              placeholder={t("lastName")}
                              value={guestInput.surname}
                              onChange={(e) => {
                                setGuestInput({ ...guestInput, surname: e.target.value });
                                setErrors((prev) => ({ ...prev, lastname: "" }));
                              }}
                            />
                            {errors.lastname && (

                              <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.lastname}</p></div>
                            )}
                          </div>
                        </div>
                        <div className="form-row border-0">
                          <div className="form-col-1">
                            <label className="popup-inner-title m-0">{t("email") + "*"}</label>
                          </div>
                          <div className="form-col-2 pt-0">
                            <div className="position-relative">
                            <input
                              type="email"
                              className={`form-control 
                              ${errors.email ? "error-form" : ""}
                              ${!errors.email && guestInput.email ? "success-validation" : ""}
                              `}
                              autoComplete="off"
                              //placeholder={t("react_info") + "@"}
                              placeholder={t("email")}
                              value={guestInput.email}
                              onChange={(e) => {
                                setGuestInput({ ...guestInput, email: e.target.value });
                                setErrors((prev) => ({ ...prev, email: "" }));
                              }}
                            />
                            <img
                              src={Images.EmailMessage}
                              // Hidden while the input shows its own right-side icon - the red error icon
                              // (error-form) or the green success tick (success-validation) - so they don't overlap
                              className={`form-icon ${errors.email || guestInput.email ? "d-none" : ""}`}
                            />
                            {errors.email && (

                              <div className="error-text"><img src={Images.ErrorMsg}></img> <p>{errors.email}</p></div>
                            )}
                            </div>
                          </div>
                        </div>
                        <div className="form-row border-0 align-items-start">
                          <div className="form-col-1">
                            <label className="popup-inner-title m-0 pt-3" htmlFor="name">{t("national_id")} ({t("optional")})</label>
                          </div>
                          <div className="form-col-2 pt-0">
                            <input
                              type="text"
                              className="form-control"
                              autoComplete="off"
                              placeholder={t("national_id") + " (" + t("optional") + ")"}
                              value={guestInput.nationalIdentificationId}
                              onChange={(e) => setGuestInput({ ...guestInput, nationalIdentificationId: e.target.value })}
                            />

                            {errors.guestCapacity && (
                              <div className="error-text">
                                <img src={Images.ErrorMsg} alt="" />
                                <p>{errors.guestCapacity}</p>
                              </div>
                            )}
                            <div className="modal-btn d-flex justify-content-end gap-3 mt-3">

                              <a href="#collapseExample" className="main-btn outline-gray-btn"
                                onClick={() => {
                                  setShowGuestForm(false);
                                  setGuestInput({ name: "", surname: "", email: "" });
                                }} >{t("cancel")}</a>
                              <a
                                href="#"
                                className="main-btn fill-green-btn"
                                onClick={handleConfirmExternalGuest}
                              >
                                {t("save")}
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/*------------------All Participants-list------------------*/}
                    <div className="form-row border-top align-items-start">
                      <div className="form-col-1 mt-2 pt-4">

                        <label className="popup-inner-title m-0">{t("react_participants")}</label>
                      </div>
                      <div className="form-col-2 participants-list mt-0 border-top-0">

                        <div className="form-check">
                          <label className="form-check-label first-check-label">
                            {PFirstName} {PLastName} ({PEmail})
                          </label>
                        </div>
                        {selectedEmployees.length > 0 &&
                          selectedEmployees.map((emp) => (
                            <div key={emp.EmployeeId}
                              className={`form-check d-flex align-items-center ${checkedEmployees.includes(emp.EmployeeId) ? "check-active" : ""}`}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`emp-${emp.EmployeeId}`}
                                checked={checkedEmployees.includes(emp.EmployeeId)}
                                onChange={() => handleCheckboxChange(emp.EmployeeId)}
                              />

                              <label
                                className="form-check-label ms-2"
                                htmlFor={`emp-${emp.EmployeeId}`}
                              >
                                {emp.EmployeeName}{emp.EmployeeEmail ? ` (${emp.EmployeeEmail})` : ""}
                              </label>

                              <a
                                className={`btn btn-link p-0 ms-2 check-delete ${checkedEmployees.includes(emp.EmployeeId) ? "opacity-1" : "opacity-0"}`}
                                //className="btn btn-link p-0 ms-2"
                                onClick={() => handleRemoveEmployee(emp.EmployeeId)}
                                disabled={!checkedEmployees.includes(emp.EmployeeId)} // optional UX
                                aria-label={`Remove ${emp.EmployeeName}`}
                              >
                                <img src={Images.Trash} className="img-fluid" alt="delete" />
                              </a>
                            </div>
                          ))}

                        {Array.isArray(participants) && participants.length > 0 &&
                          participants.map((p) => (
                            <div
                              className={`form-check form-active d-flex align-items-center ${checkedParticipants.includes(p.id) ? "check-active" : ""
                                }`}
                              key={p.id}>
                              <input
                                className="form-check-input"
                                type="checkbox"
                                id={`participant-${p.id}`}
                                checked={checkedParticipants.includes(p.id)}
                                onChange={() => handleParticipantCheckboxChange(p.id)}
                              />

                              <label
                                className="form-check-label ms-2"
                                htmlFor={`participant-${p.id}`}
                              >
                                {p.name} ({p.email})
                              </label>

                              <a
                                type="button"
                                //className="btn btn-link p-0 ms-2"
                                className={`btn btn-link p-0 ms-2 check-delete ${checkedParticipants.includes(p.id) ? "opacity-1" : "opacity-0"}`}
                                onClick={() => handleRemoveParticipant(p.id)}
                                disabled={!checkedParticipants.includes(p.id)} // UX improvement
                                aria-label={`Remove ${p.name}`}
                              >
                                <img src={Images.Trash} className="img-fluid" alt="delete" />
                              </a>
                            </div>
                          ))}

                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 3 */}
                {currentSection === 2 && (
                  <div className="section active confirmation-section">
                    {/* Approval notice. Replaces the old step 4: the warning now
                        shows BEFORE Confirm rather than after the booking was
                        already created. */}
                    {needApproval && (
                      <>
                        <h4 className="text-red text-center approval-notice">
                          {t("react_booking_requires_approval")}
                        </h4>
                        <div className="divider-line approval-divider" />
                      </>
                    )}


                      <div className="icon-with-list mb-0">
                        {/* Approver when approval applies, otherwise the organiser -
                            one row either way, per the design. */}
                        <div className="icon-wrap">
                          <img
                            src={Images.SheildTick}
                            className="img-fluid"
                            alt={needApproval ? "approver" : "organizer"}
                          />
                          <h4 className={needApproval ? "approver-line" : ""}>
                            {needApproval
                              ? formatReactString(
                                t(
                                  approverList.length > 1
                                    ? "react_create_meeting_approvers"
                                    : "react_create_meeting_approver"
                                ),
                                [approverList.join(", ")],
                                {
                                  defaultRenderer: (value) => (
                                    <span className="approver-names">{value}</span>
                                  )
                                }
                              )
                              : `${PFirstName} ${PLastName}`}
                          </h4>
                        </div>

                        <div className="icon-wrap">
                          <img src={Images.GreenCalender} className="img-fluid" alt="date" />
                          <h4>
                            {displaySelectedDate}
                          </h4>

                        </div>

                        <div className="icon-wrap">
                          <img src={Images.TimeIcon} className="img-fluid" alt="time" />
                          <h4>
                            {t("from")} <span className="zone_text">{selectedFrom || "00:00"}</span> {t("to")} <span className="zone_text">{selectedTo || "00:00"}</span>
                          </h4>
                        </div>

                        <div className="icon-wrap align-items-start">
                          <img src={Images.ZoneMap ?? Images.GreenCalender} className="img-fluid" alt="spot" />
                          <h4>
                            {selectedSpotObj?.SpotName}
                            <span className="zone_text"> {meetingName}</span>
                          </h4>
                        </div>

                        <div className="icon-wrap">
                          <img src={Images.Participant} className="img-fluid" alt="participants" />
                          <h4>
                            {`${1 + (participants?.length || 0) + (selectedEmployees?.length || 0)} ${t(
                              1 + (participants?.length || 0) + (selectedEmployees?.length || 0) === 1
                                ? "react_create_meeting_participant"
                                : "react_create_meeting_participants"
                            )}`}
                          </h4>
                        </div>

                        {/* Request / release windows, parsed from ApprovalInfo. Inside
                            the list so they share its centred column and line up with
                            the row text (past the icon), as in the design. */}
                        {needApproval && (
                          <div className="approval-window text-red">
                            <p>
                              {t("react_request_must_be_made_before_start", {
                                hours: requestWindow.hours,
                                minutes: requestWindow.minutes
                              })}
                            </p>
                            <p>
                              {t("react_unapproved_request_release_time", {
                                hours: releaseWindow.hours,
                                minutes: releaseWindow.minutes
                              })}
                            </p>
                          </div>
                        )}
                      </div>

                    {/* Illustration sits below the detail list and only in create
                        mode; the edit confirmation shows the attendee-update card
                        in its place. */}
                    {!isEdit && (
                      <div className="modal_img text-center">
                        <img src={Images.Meeting} className="img-fluid" alt="meeting" />
                      </div>
                    )}

                    {/* Edit mode: attendee-update choice — shown only when the
                        attendee list changed vs the original meeting. */}
                    {isEdit && attendeesChanged && (
                      <div className="attendee-wrap">
                        <p>{t("made_changes_updates_for_attendees")}</p>
                        <button
                          type="button"
                          className={`btn-select w-100 ${sendUpdateChoice === "added_removed" ? "btn-select-green" : "btn-select-gray"}`}
                          onClick={() => setSendUpdateChoice("added_removed")}
                          aria-pressed={sendUpdateChoice === "added_removed"}
                        >
                          {t("only_added_removed_attendees")}
                        </button>
                        <button
                          type="button"
                          className={`btn-select w-100 ${sendUpdateChoice === "all" ? "btn-select-green" : "btn-select-gray"}`}
                          onClick={() => setSendUpdateChoice("all")}
                          aria-pressed={sendUpdateChoice === "all"}
                        >
                          {t("all_attendess")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {/* STEP 4 (approval) retired in RON-5827 - the approval notice
                    now lives on the confirmation step above, shown BEFORE Confirm
                    instead of after the booking was already created. Kept,
                    commented, until signed off.

                
                {needApproval && currentSection === 3 && (
                  <div className="section active">
                    <div className="icon-with-list mb-0 mt-3">
                      {/_* APPROVER *_/}
                      <div className="icon-wrap mb-3">
                        <img src={Images.SheildTick} className="img-fluid" />
                        <h4>
                          <span className="zone_text">
                            {t(
                              approverList.join(", ").includes(",")
                                ? "react_create_meeting_approvers"
                                : "react_create_meeting_approver"
                            ).split("%s")[0]}
                          </span>
                          {approverList.join(", ")}
                          <span className="zone_text">
                            {t(
                              approverList.join(", ").includes(",")
                                ? "react_create_meeting_approvers"
                                : "react_create_meeting_approver"
                            ).split("%s")[1] || ""}
                          </span>
                        </h4>
                      </div>
                      {/_* DATE *_/}
                      <div className="icon-wrap mb-3">
                        <img src={Images.GreenCalender} className="img-fluid" />
                        <h4>
                          {displaySelectedDate}</h4>
                      </div>
                      {/_* APPROVAL INFO *_/}
                      <div className="icon-wrap d-flex align-items-start">
                        <img src={Images.TimeIcon} className="img-fluid mt-1" />
                        {/_* <h4><span className="zone_text d-block mb-1">Requests must be made at least 02 hours before start time. </span>An unapproved request will be released 01 hour before start time</h4> *_/}
                        {/_* <h4><span className="zone_text d-block mb-1"></span>{approvalInfo}</h4> *_/}
                        <h4>
                          <span className="zone_text d-block mb-1">
                            {/_* Requests must be made at least {requestWindow.hours} hours, {requestWindow.minutes} minutes before start time. *_/}
                            <span className="zone_text d-block mb-1">
                              {t("react_request_must_be_made_before_start", {
                                hours: requestWindow.hours,
                                minutes: requestWindow.minutes
                              })}
                            </span>

                          </span>

                          {/_* An unapproved request will be released {releaseWindow.hours} hour{Number(releaseWindow.hours) === 1 ? "" : "s"}, {releaseWindow.minutes} minutes before start time *_/}
                          <span className="zone_text d-block mb-1" style={{ color: "#000" }}>
                            {t("react_unapproved_request_release_time", {
                              hours: releaseWindow.hours,
                              minutes: releaseWindow.minutes
                            })}

                          </span>

                        </h4>

                      </div>
                    </div>
                    <div className="modal_img text-center mb-3 pb-1"><img src={Images.Unapproved} className="img-fluid" /></div>
                  </div>
                )}
                */}

                {/* Footer Buttons - pinned to the bottom of the form column by
                .offcanvas-col-form form .btn-wrap. `tags-btn` is kept so the
                existing button styling and the #backBtn.disable rule still apply. */}
                <div className="btn-wrap tags-btn gap-3">

                  {/* BACK BUTTON */}
                  {/* {currentSection > 0 && currentSection < 3 && ( */}
                  {currentSection < 3 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={handleBack}
                      id="backBtn"
                    >
                      {t("back")}
                    </button>
                  )}

                  {/* NEXT BUTTON */}
                  {currentSection < 2 && (
                    <button
                      type="button"
                      className={`btn btn-success ${(currentSection === 0 && isStep1NextDisabled) ? "disabled" : "selected"}`}
                      onClick={handleNext}
                      id="nextBtn"
                      disabled={currentSection === 0 && isStep1NextDisabled}
                    >
                      {t("next")}
                    </button>
                  )}

                  {/* CONFIRM BUTTON */}
                  {currentSection === 2 && (
                    <button
                      type="button"
                      className="main-btn outline-green-btn selected"
                      onClick={() => {
                        if (!isLoading) {
                          handleConfirm();
                        }
                      }}
                    >
                      {t("ok")}
                    </button>
                  )}

                </div>
              </form>
            </div>
            <div className={`offcanvas-col-time-grid form-row ${currentSection === 0 ? "" : "d-none"}`}>
              <div
                ref={setCalendarScrollEl}
                style={{
                  overflowY: "auto",
                  background: "#fff",
                  pointerEvents: isCalendarInteractive ? "auto" : "none",
                  opacity: isCalendarInteractive ? 1 : 0.65,
                }}
              >
                <FullCalendar
                  key={`${selectedDate?.getFullYear()}-${selectedDate?.getMonth()}-${selectedDate?.getDate()}-${spot}-${timeIntervals.start}-${timeIntervals.end}`}
                  ref={calendarRef}
                  plugins={[timeGridPlugin, interactionPlugin]}
                  initialView="timeGridDay"
                  initialDate={selectedDate}
                  height="auto"
                  headerToolbar={false}
                  nowIndicator={true}
                  allDaySlot={false}
                  slotMinTime={timeIntervals.start}
                  slotMaxTime={timeIntervals.end}
                  selectable={isCalendarInteractive}
                  unselectAuto={false}
                  dayHeaders={true}
                  dayHeaderContent={(arg) => t(WEEKDAY_LONG_KEYS[arg.date.getDay()])}
                  // selectAllow={(selectInfo) => selectInfo.start >= new Date()}
                  events={events} // Pass events to FullCalendar
                  selectAllow={(selectInfo) => {
                    if (!isCalendarInteractive) {
                      return false;
                    }

                    // Prevent selection of booked slots
                    const selectedStart = selectInfo.start;
                    const selectedEnd = selectInfo.end;
                    const selectedStartTime = selectInfo.startStr.slice(11, 16);
                    const selectedEndTime = selectInfo.endStr.slice(11, 16);

                    if (isBlockedBookingSlot(selectedStartTime, selectedEndTime)) {
                      return false;
                    }

                    // Check if the selected slot overlaps with any booked slot
                    const isOverlapping = events.some((event) => {
                      const eventStart = new Date(event.start);
                      const eventEnd = new Date(event.end);
                      return (
                        (selectedStart >= eventStart && selectedStart < eventEnd) || // Start overlaps
                        (selectedEnd > eventStart && selectedEnd <= eventEnd) || // End overlaps
                        (selectedStart <= eventStart && selectedEnd >= eventEnd) // Fully overlaps
                      );
                    });

                    return !isOverlapping; // Allow selection only if no overlap
                  }}
                  select={(info) => {
                    if (!isCalendarInteractive) {
                      return;
                    }

                    const from = info.startStr.slice(11, 16);
                    const to = info.endStr.slice(11, 16);

                    ignoreNextDateClick.current = true;

                    if (isProgrammaticSelection.current) {
                      isProgrammaticSelection.current = false;

                      // Ignore only the mirrored calendar sync callback.
                      if (from === selectedFrom && to === selectedTo) {
                        return;
                      }
                    }

                    setErrors((prev) => ({
                      ...prev,
                      timeInvalid: "",
                    }));
                    setSelectedFrom(from);
                    setSelectedTo(to);
                    setIsTimeSlotSelected(true);
                  }}
                  dateClick={(info) => {
                    if (!isCalendarInteractive) {
                      return;
                    }

                    if (ignoreNextDateClick.current) {
                      ignoreNextDateClick.current = false;
                      return;
                    }

                    const from = info.dateStr.slice(11, 16);
                    const endDate = new Date(info.date);
                    endDate.setMinutes(endDate.getMinutes() + 30);
                    const to = endDate.toTimeString().slice(0, 5);

                    if (isBlockedBookingSlot(from, to)) {
                      return;
                    }

                    const isOverlapping = events.some((event) => {
                      const eventStart = new Date(event.start);
                      const eventEnd = new Date(event.end);
                      return info.date >= eventStart && info.date < eventEnd;
                    });

                    if (isOverlapping) {
                      return;
                    }

                    setErrors((prev) => ({
                      ...prev,
                      timeInvalid: "",
                    }));
                    setSelectedFrom(from);
                    setSelectedTo(to);
                    setIsTimeSlotSelected(true);
                  }}

                  slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                  eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}

                  viewDidMount={applyBlockedTimeSlotClasses}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {renderApiUi(isLoading)}
    </>
  );
};

export default NewMeetingModal;
